import { useEffect, useRef } from 'react'

import { BiddingJepxReconcileOutcome } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import { appToaster } from '~/libs/toaster'
import { convertAreaLabel } from '~/models/area'
import {
  type SendObservation,
  type SendResultNoticeKind,
  type SendResultSubject,
  observeReconcileOutcome,
  observeSendResult,
} from '~/pages/SpotBidding/models/sendResultNotice'

const NOTICE_KEY: Record<SendResultNoticeKind, string> = {
  retrying: 'page:biddingSpot.sendResultToast.retrying',
  limitExceeded: 'page:biddingSpot.sendResultToast.limitExceeded',
  approvalFlowChanged: 'page:biddingSpot.sendResultToast.approvalFlowChanged',
  jepxRejected: 'page:biddingSpot.sendResultToast.jepxRejected',
  memberInfoMissing: 'page:biddingSpot.sendResultToast.memberInfoMissing',
  workflowNotSent: 'page:biddingSpot.sendResultToast.workflowNotSent',
  workflowJepxRejected: 'page:biddingSpot.sendResultToast.workflowJepxRejected',
  deadlineUnreached: 'page:biddingSpot.sendResultToast.deadlineUnreached',
  reconcileBackToBidding:
    'page:biddingSpot.sendResultToast.reconcileBackToBidding',
  reconcileDuplicate: 'page:biddingSpot.sendResultToast.reconcileDuplicate',
  reconcileToAwaitingMatch:
    'page:biddingSpot.sendResultToast.reconcileToAwaitingMatch',
  reconcileWithdrawSent:
    'page:biddingSpot.sendResultToast.reconcileWithdrawSent',
}

/**
 * 1 件の通知の単位。 通常入札は受渡日・エリアごと (AC-BSP-130 の文言の単位)、 ブロック入札は
 * LINK / LOOP の 1 組で 1 件とし、 組のエリアを「・」 で並べる (AC-BSP-132 の並べ方)。
 */
export type SendResultNoticeUnit = 'perArea' | 'perGroup'

/**
 * 予約入札の受付開始時の自動送信、 提出・承認の後の送信の結果を、 開いている画面で
 * 観測した時に知らせる (AC-BSP-130)。 一覧の polling ごと (`observedAtMs` = 一覧の取得時刻)
 * に観測を進める。
 *
 * 画面を見ていない利用者にも伝わるよう、 自動では閉じず、 閉じるまで残す
 * (AC-BSP-130「エラートーストだけでは読む前に消える」)。 一覧の polling は画面が
 * 隠れている間は止まり、 戻った時の再取得で観測するので、 戻った利用者が読める。
 * design (design/bidding/spot.html) はお知らせ (ticker・あなたへのお知らせ) にも出すが、
 * 本番の画面にはお知らせへ画面から登録する口がまだ無いので、 toast だけで出す (#11186)。
 *
 * JEPX に届いたかを確かめた結果の失効 (一覧の軸は変わらない) は、 明細の取り直し
 * (`findStaleApprovalDetailIds` の `isFailedFinalSettledSince`) で subjects が作り直され、
 * ここの観測が走ることに依存する。 その配線が外れると知らせが出ない。
 */
export function useSendResultNotice(
  subjects: readonly SendResultSubject[] | undefined,
  observedAtMs: number,
  unit: SendResultNoticeUnit,
  /** 取得の対象の受渡日 (年-月-日)。 変わったら照合の結果の観測を最初の取得に戻す。 */
  scope: string,
): void {
  const { t } = useTranslation()
  const observations = useRef(new Map<string, SendObservation>())
  const reconcileOutcomes = useRef(
    new Map<string, BiddingJepxReconcileOutcome>(),
  )
  // 照合の結果は受渡日ごとに観測する。 受渡日を切り替えた先のセットは、 前に照合済みの
  // 古い結果であって「分かった時」ではないので、 切り替え後の最初の取得は知らせない。
  const reconcileScope = useRef(scope)
  const reconcileFirstFetch = useRef(true)

  useEffect(() => {
    if (subjects === undefined) return
    if (reconcileScope.current !== scope) {
      reconcileScope.current = scope
      reconcileOutcomes.current.clear()
      reconcileFirstFetch.current = true
    }
    // key → 文言。 通常入札は同じ受渡日・エリアで同じ時点に決まった結果を 1 件にまとめ、
    // ブロック入札は組ごとに 1 件出す (2 本の通知だけは同じ文言を 1 件にまとめる。 AC-BSP-130)。
    const titles = new Map<string, string>()
    const reconcileFirst = reconcileFirstFetch.current
    reconcileFirstFetch.current = false
    // 照合の結果は、 明細が一覧と同じ照合の結果になるまで、 明細の札が照合前の内容・状態
    // (失効の表示など) のままで通知の根拠が古いので、 通知も観測値の更新も保留し、 次の
    // 取得で判定し直す。 保留は通知を束ねる単位ごと: 2 本 (DUPLICATE_ON_JEPX) の 2 セットは
    // 1 件にまとめるので全部の明細が揃った取得で判定し、 そのほかのセットは自分の明細だけで
    // 判定する (明細が届かないセットが、 ほかのセットの通知を止めない)。
    const detailReady = (s: SendResultSubject): boolean =>
      s.detail !== undefined &&
      s.detail.jepxReconcileOutcome === s.jepxReconcileOutcome
    const isDuplicate = (s: SendResultSubject): boolean =>
      s.jepxReconcileOutcome === BiddingJepxReconcileOutcome.DUPLICATE_ON_JEPX
    const duplicatesReady = subjects.filter(isDuplicate).every(detailReady)
    const reconcileReadyOf = (s: SendResultSubject): boolean =>
      detailReady(s) && (!isDuplicate(s) || duplicatesReady)
    for (const subject of subjects) {
      const observed = observeSendResult(
        observations.current.get(subject.id),
        subject,
        observedAtMs,
      )
      const { next } = observed
      observations.current.set(subject.id, next)
      let reconcileNotice: SendResultNoticeKind | undefined
      if (reconcileFirst || reconcileReadyOf(subject)) {
        reconcileNotice = observeReconcileOutcome(
          reconcileOutcomes.current.get(subject.id),
          subject.jepxReconcileOutcome,
          reconcileFirst,
        )
        reconcileOutcomes.current.set(subject.id, subject.jepxReconcileOutcome)
      }
      const notice = observed.notice ?? reconcileNotice
      if (
        notice === undefined ||
        subject.detail === undefined ||
        subject.targetDate === undefined
      ) {
        continue
      }
      // {受渡日} は月/日 (design/bidding/spot.html の AC-BSP-130 のお知らせと同じ書式)。
      const date = `${subject.targetDate.month}/${subject.targetDate.day}`
      const areaLabels = subject.detail.areaCodes.map(convertAreaLabel)
      if (unit === 'perGroup') {
        const area = areaLabels.join('・')
        const title = t(NOTICE_KEY[notice], { date, area })
        // 2 本の 2 グループは同じ文言を 1 件にまとめる。 そのほかの結果は組ごと。
        titles.set(notice === 'reconcileDuplicate' ? title : subject.id, title)
        continue
      }
      for (const area of areaLabels) {
        const title = t(NOTICE_KEY[notice], { date, area })
        titles.set(title, title)
      }
    }
    // toast は 1 件ずつ別の task で出す。 DS の toaster (zag-js の toast group) は同じ task で
    // 続けて作った toast を同じ前の状態に足すので、 最後の 1 件しか残らない。
    for (const title of titles.values()) {
      setTimeout(() => {
        appToaster.create({
          type: 'warning',
          title,
          duration: Infinity,
          closable: true,
        })
      }, 0)
    }
  }, [subjects, observedAtMs, unit, scope, t])
}
